import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { habitAPI } from '../lib/supabase' 

const { FiX, FiSave, FiTarget, FiCalendar, FiTag, FiType, FiTrendingUp, FiClock, FiActivity, FiChevronLeft, FiChevronRight } = FiIcons 

const HabitForm = ({ isOpen, onClose, onSave, habit = null }) => {
  const [formData, setFormData] = useState({
    title: habit?.title || '',
    description: habit?.description || '',
    category: habit?.category || 'wellness',
    icon: habit?.icon || '🎯',
    target_days: habit?.target_days || 30,
    goal_amount: habit?.goal_amount || 1,
    goal_unit: habit?.goal_unit || 'session',
    frequency: habit?.frequency || 'daily'
  }) 
  const [saving, setSaving] = useState(false) 
  const [currentStep, setCurrentStep] = useState(0) 
  const [error, setError] = useState('') // Added missing state variables

  const categories = [ 
    { id: 'wellness', name: 'Wellness', icon: '🧘', color: 'from-emerald-400 to-green-600', description: 'Mental health, mindfulness, self-care' },
    { id: 'fitness', name: 'Fitness', icon: '💪', color: 'from-blue-400 to-indigo-600', description: 'Exercise, sports, physical activity' },
    { id: 'learning', name: 'Learning', icon: '📚', color: 'from-purple-400 to-violet-600', description: 'Education, skills, knowledge' },
    { id: 'productivity', name: 'Productivity', icon: '⚡', color: 'from-forge-400 to-orange-500', description: 'Work, organization, efficiency' },
    { id: 'social', name: 'Social', icon: '👥', color: 'from-pink-400 to-rose-600', description: 'Relationships, communication, community' },
    { id: 'creativity', name: 'Creativity', icon: '🎨', color: 'from-indigo-400 to-purple-600', description: 'Art, music, creative expression' } 
  ] 

  const commonIcons = [ 
    { emoji: '🎯', category: 'general', name: 'Target' },
    { emoji: '💪', category: 'fitness', name: 'Strength' },
    { emoji: '📚', category: 'learning', name: 'Read' },
    { emoji: '🧘', category: 'wellness', name: 'Meditate' },
    { emoji: '🏃', category: 'fitness', name: 'Run' },
    { emoji: '💡', category: 'learning', name: 'Ideas' },
    { emoji: '🎨', category: 'creativity', name: 'Art' },
    { emoji: '🌱', category: 'wellness', name: 'Grow' },
    { emoji: '☀️', category: 'wellness', name: 'Morning' },
    { emoji: '🔥', category: 'general', name: 'Passion' },
    { emoji: '⭐', category: 'general', name: 'Excellence' },
    { emoji: '🚀', category: 'productivity', name: 'Launch' },
    { emoji: '📝', category: 'productivity', name: 'Write' },
    { emoji: '🎵', category: 'creativity', name: 'Music' },
    { emoji: '🍎', category: 'wellness', name: 'Nutrition' },
    { emoji: '💤', category: 'wellness', name: 'Sleep' },
    { emoji: '🧠', category: 'learning', name: 'Think' },
    { emoji: '❤️', category: 'social', name: 'Love' },
    { emoji: '🌟', category: 'general', name: 'Shine' },
    { emoji: '⚡', category: 'productivity', name: 'Energy' } 
  ] 

  const targetOptions = [ 
    { days: 7, label: '1 Week', description: 'Perfect for trying new habits' },
    { days: 14, label: '2 Weeks', description: 'Build initial momentum' },
    { days: 21, label: '3 Weeks', description: 'Form a habit (classic)' },
    { days: 30, label: '1 Month', description: 'Establish routine' },
    { days: 60, label: '2 Months', description: 'Strengthen habit' },
    { days: 90, label: '3 Months', description: 'Transform lifestyle' } 
  ] 

  const goalUnits = [ 
    { id: 'minutes', name: 'Minutes', icon: '⏱️', description: 'Time-based goals' },
    { id: 'hours', name: 'Hours', icon: '🕐', description: 'Longer time commitments' },
    { id: 'pages', name: 'Pages', icon: '📄', description: 'Reading or writing goals' },
    { id: 'sessions', name: 'Sessions', icon: '🎯', description: 'Activity completions' },
    { id: 'reps', name: 'Repetitions', icon: '🔄', description: 'Exercise or practice' },
    { id: 'glasses', name: 'Glasses', icon: '💧', description: 'Water intake' },
    { id: 'steps', name: 'Steps', icon: '👣', description: 'Walking goals' },
    { id: 'kilometers', name: 'Kilometers', icon: '🏃', description: 'Distance goals' } 
  ] 

  const frequencyOptions = [ 
    { id: 'daily', name: 'Daily', description: 'Every day' },
    { id: 'weekdays', name: 'Weekdays', description: 'Monday to Friday' },
    { id: 'weekly', name: 'Weekly', description: 'Once per week' },
    { id: 'custom', name: 'Custom', description: 'Select specific days' } 
  ] 

  const steps = [ 
    { title: 'Basic Info', icon: FiType, description: 'Name and describe your habit' },
    { title: 'Category', icon: FiTag, description: 'Choose a category' },
    { title: 'Icon', icon: FiTarget, description: 'Pick a visual representation' },
    { title: 'Goal', icon: FiTrendingUp, description: 'Set your daily goal' },
    { title: 'Duration', icon: FiCalendar, description: 'Set your target duration' } 
  ] 

  const handleSubmit = async (e) => {
    e.preventDefault() 
    if (!formData.title.trim()) {
      setError('Please enter a habit title')
      return 
    }
    
    setError('')
    setSaving(true) 
    
    try {
      let result 
      if (habit) {
        result = await habitAPI.updateHabit(habit.id, formData)
      } else {
        result = await habitAPI.createHabit(formData)
      } 
      
      if (result.error) {
        console.error('Error saving habit:', result.error) 
        setError(result.error)
        return
      } 
      
      console.log('Habit saved successfully:', result.data)
      onSave(result.data) 
      onClose()
    } catch (error) {
      console.error('Error saving habit:', error) 
      setError('Failed to save habit. Please try again.')
    } finally {
      setSaving(false)
    } 
  } 

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (error) setError('')
  } 

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } 
  } 

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    } 
  } 

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.title.trim().length > 0 
      case 1: return formData.category 
      case 2: return formData.icon 
      case 3: return formData.goal_amount > 0 
      case 4: return formData.target_days > 0 
      default: return true
    } 
  } 

  // Return nothing if not open - this ensures the modal doesn't render at all
  if (!isOpen) return null 

  // The key to fixing mobile display is ensuring the modal takes up the full screen on mobile
  // and has proper z-index and positioning
  return ( 
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" style={{touchAction: 'none'}}> 
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }} 
        exit={{ opacity: 0, scale: 0.9, y: 20 }} 
        className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm rounded-xl shadow-dominance w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-forge-400/30"
        style={{
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch', // For iOS momentum scrolling
          position: 'relative', // Ensure proper stacking context
        }}
      > 
        {/* Header */} 
        <div className="bg-forge-gradient p-4 sm:p-6 text-shadow-950 sticky top-0 z-10"> 
          <div className="flex items-center justify-between mb-4"> 
            <h2 className="text-xl font-bold"> 
              {habit ? 'Edit Habit' : 'Create New Habit'} 
            </h2> 
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-shadow-900/20 rounded-xl transition-colors"
              type="button" 
              aria-label="Close"
            > 
              <SafeIcon icon={FiX} className="text-xl" /> 
            </button> 
          </div> 
          
          {/* Progress Steps - Simplified for mobile */} 
          <div className="flex items-center space-x-1">
            {steps.map((step, index) => ( 
              <div key={index} className="flex items-center"> 
                <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${index <= currentStep ? 'bg-shadow-950 text-forge-400' : 'bg-shadow-900/20 text-shadow-950/60'}`}> 
                  {index + 1} 
                </div> 
                {index < steps.length - 1 && ( 
                  <div className={`w-2 sm:w-4 h-0.5 transition-all duration-300 ${index < currentStep ? 'bg-shadow-950' : 'bg-shadow-900/20'}`} /> 
                )} 
              </div> 
            ))} 
          </div> 
        </div> 
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/20 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm mb-4"
            >
              {error}
            </motion.div>
          )}

          <AnimatePresence mode="wait"> 
            {/* Step 1: Basic Info */} 
            {currentStep === 0 && ( 
              <motion.div 
                key="step1" 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }} 
                className="space-y-6" 
              > 
                <div className="text-center mb-6"> 
                  <SafeIcon icon={FiType} className="text-3xl text-forge-400 mx-auto mb-2" /> 
                  <h3 className="text-xl font-semibold text-forge-400">Basic Information</h3> 
                  <p className="text-forge-300">Give your habit a name and description</p> 
                </div> 
                
                <div> 
                  <label className="block text-sm font-medium text-forge-300 mb-2"> 
                    Habit Title * 
                  </label> 
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={(e) => handleChange('title', e.target.value)} 
                    className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50" 
                    placeholder="e.g., Morning Meditation" 
                    required
                  /> 
                </div> 
                
                <div> 
                  <label className="block text-sm font-medium text-forge-300 mb-2"> 
                    Description 
                  </label> 
                  <textarea 
                    value={formData.description} 
                    onChange={(e) => handleChange('description', e.target.value)} 
                    className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 resize-none bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50" 
                    rows="3" 
                    placeholder="Brief description of your habit and why it matters to you..." 
                  /> 
                </div> 
                
                <div> 
                  <label className="block text-sm font-medium text-forge-300 mb-2"> 
                    Frequency 
                  </label> 
                  <div className="grid grid-cols-2 gap-3"> 
                    {frequencyOptions.map(freq => ( 
                      <button 
                        key={freq.id} 
                        type="button" 
                        onClick={() => handleChange('frequency', freq.id)} 
                        className={`p-3 rounded-xl text-left transition-all duration-300 ${formData.frequency === freq.id ? 'bg-forge-gradient text-shadow-950 shadow-forge' : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/30 text-forge-200'}`} 
                      > 
                        <div className="font-semibold">{freq.name}</div> 
                        <div className={`text-sm ${formData.frequency === freq.id ? 'text-shadow-950/80' : 'text-forge-300'}`}> 
                          {freq.description} 
                        </div> 
                      </button> 
                    ))} 
                  </div> 
                </div> 
              </motion.div> 
            )} 

            {/* Step 2: Category */} 
            {currentStep === 1 && ( 
              <motion.div 
                key="step2" 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }} 
                className="space-y-6" 
              > 
                <div className="text-center mb-6"> 
                  <SafeIcon icon={FiTag} className="text-3xl text-forge-400 mx-auto mb-2" /> 
                  <h3 className="text-xl font-semibold text-forge-400">Choose Category</h3> 
                  <p className="text-forge-300">Select the category that best fits your habit</p> 
                </div> 
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"> 
                  {categories.map(category => ( 
                    <motion.button 
                      key={category.id} 
                      type="button" 
                      whileHover={{ scale: 1.02 }} 
                      whileTap={{ scale: 0.98 }} 
                      onClick={() => handleChange('category', category.id)} 
                      className={`p-4 rounded-xl text-left transition-all duration-300 ${formData.category === category.id ? `bg-gradient-to-r ${category.color} text-white shadow-lg` : 'bg-shadow-800/50 hover:bg-shadow-700/50 text-forge-200 border border-forge-400/30'}`} 
                    > 
                      <div className="flex items-center space-x-3 mb-2"> 
                        <span className="text-2xl">{category.icon}</span> 
                        <span className="font-semibold">{category.name}</span> 
                      </div> 
                      <p className={`text-sm ${formData.category === category.id ? 'text-white/80' : 'text-forge-300'}`}> 
                        {category.description} 
                      </p> 
                    </motion.button> 
                  ))} 
                </div> 
              </motion.div> 
            )} 

            {/* Step 3: Icon */} 
            {currentStep === 2 && ( 
              <motion.div 
                key="step3" 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }} 
                className="space-y-6" 
              > 
                <div className="text-center mb-6"> 
                  <SafeIcon icon={FiTarget} className="text-3xl text-forge-400 mx-auto mb-2" /> 
                  <h3 className="text-xl font-semibold text-forge-400">Choose Icon</h3> 
                  <p className="text-forge-300">Pick an emoji that represents your habit</p> 
                </div> 
                
                <div className="grid grid-cols-5 sm:grid-cols-8 gap-3"> 
                  {commonIcons.map(icon => ( 
                    <motion.button 
                      key={icon.emoji} 
                      type="button" 
                      whileHover={{ scale: 1.1 }} 
                      whileTap={{ scale: 0.9 }} 
                      onClick={() => handleChange('icon', icon.emoji)} 
                      className={`p-3 rounded-xl text-2xl transition-all duration-300 ${formData.icon === icon.emoji ? 'bg-forge-gradient shadow-lg ring-2 ring-forge-300' : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/30'}`} 
                      title={icon.name} 
                    > 
                      {icon.emoji} 
                    </motion.button> 
                  ))} 
                </div> 
              </motion.div> 
            )} 

            {/* Step 4: Goal */} 
            {currentStep === 3 && ( 
              <motion.div 
                key="step4" 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }} 
                className="space-y-6" 
              > 
                <div className="text-center mb-6"> 
                  <SafeIcon icon={FiTrendingUp} className="text-3xl text-forge-400 mx-auto mb-2" /> 
                  <h3 className="text-xl font-semibold text-forge-400">Set Your Daily Goal</h3> 
                  <p className="text-forge-300">How much do you want to do each day?</p> 
                </div> 
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
                  <div> 
                    <label className="block text-sm font-medium text-forge-300 mb-2"> 
                      Goal Amount 
                    </label> 
                    <input 
                      type="number" 
                      min="1" 
                      value={formData.goal_amount} 
                      onChange={(e) => handleChange('goal_amount', parseInt(e.target.value) || 1)} 
                      className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50" 
                      placeholder="e.g., 30" 
                    /> 
                  </div> 
                  
                  <div> 
                    <label className="block text-sm font-medium text-forge-300 mb-2"> 
                      Unit 
                    </label> 
                    <select 
                      value={formData.goal_unit} 
                      onChange={(e) => handleChange('goal_unit', e.target.value)} 
                      className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200" 
                    > 
                      {goalUnits.map(unit => ( 
                        <option key={unit.id} value={unit.id}> 
                          {unit.icon} {unit.name} 
                        </option> 
                      ))} 
                    </select> 
                  </div> 
                </div> 
                
                <div className="bg-blue-900/20 rounded-xl p-4 border border-blue-400/30"> 
                  <div className="flex items-center space-x-2 mb-2"> 
                    <SafeIcon icon={FiActivity} className="text-blue-400" /> 
                    <span className="font-medium text-blue-400">Your Goal</span> 
                  </div> 
                  <p className="text-blue-300"> 
                    Complete <strong>{formData.goal_amount} {formData.goal_unit}</strong> every day 
                  </p> 
                </div> 
              </motion.div> 
            )} 

            {/* Step 5: Duration */} 
            {currentStep === 4 && ( 
              <motion.div 
                key="step5" 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }} 
                className="space-y-6" 
              > 
                <div className="text-center mb-6"> 
                  <SafeIcon icon={FiCalendar} className="text-3xl text-forge-400 mx-auto mb-2" /> 
                  <h3 className="text-xl font-semibold text-forge-400">Set Your Target</h3> 
                  <p className="text-forge-300">How long do you want to maintain this habit?</p> 
                </div> 
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"> 
                  {targetOptions.map(option => ( 
                    <motion.button 
                      key={option.days} 
                      type="button" 
                      whileHover={{ scale: 1.02 }} 
                      whileTap={{ scale: 0.98 }} 
                      onClick={() => handleChange('target_days', option.days)} 
                      className={`p-4 rounded-xl text-left transition-all duration-300 ${formData.target_days === option.days ? 'bg-forge-gradient text-shadow-950 shadow-forge' : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/30 text-forge-200'}`} 
                    > 
                      <div className="font-semibold text-lg mb-1">{option.label}</div> 
                      <div className={`text-sm ${formData.target_days === option.days ? 'text-shadow-950/80' : 'text-forge-300'}`}> 
                        {option.description} 
                      </div> 
                    </motion.button> 
                  ))} 
                </div> 
                
                <div className="bg-gradient-to-r from-emerald-900/20 to-green-900/20 rounded-xl p-6 border border-emerald-400/30"> 
                  <div className="flex items-center space-x-2 mb-3"> 
                    <SafeIcon icon={FiTarget} className="text-emerald-400 text-xl" /> 
                    <span className="font-bold text-emerald-400 text-lg">Habit Summary</span> 
                  </div> 
                  
                  <div className="space-y-2 text-emerald-300"> 
                    <p><strong>Habit:</strong> {formData.title || 'Your habit'}</p> 
                    <p><strong>Goal:</strong> {formData.goal_amount} {formData.goal_unit} {formData.frequency}</p> 
                    <p><strong>Duration:</strong> {formData.target_days} days</p> 
                    <p><strong>Category:</strong> {categories.find(c => c.id === formData.category)?.name}</p> 
                  </div> 
                </div> 
              </motion.div> 
            )} 
          </AnimatePresence> 

          {/* Navigation Buttons - Mobile optimized */} 
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 mt-8 pt-6 border-t border-forge-400/20"> 
            <button 
              type="button" 
              onClick={prevStep} 
              disabled={currentStep === 0} 
              className="w-full sm:w-auto px-4 py-2 text-forge-300 hover:text-forge-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center sm:justify-start space-x-2" 
            > 
              <SafeIcon icon={FiChevronLeft} /> 
              <span>Previous</span> 
            </button> 
            
            <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-3 sm:space-y-0 sm:space-x-3"> 
              <button 
                type="button" 
                onClick={onClose} 
                className="w-full sm:w-auto px-4 py-2 border border-forge-400/30 text-forge-300 rounded-xl hover:bg-shadow-700/50 transition-colors" 
              > 
                Cancel 
              </button>
              
              {currentStep < steps.length - 1 ? ( 
                <button 
                  type="button" 
                  onClick={nextStep} 
                  disabled={!canProceed()} 
                  className="w-full sm:w-auto px-4 py-2 bg-forge-gradient text-shadow-950 rounded-xl hover:shadow-power disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2" 
                > 
                  <span>Next</span> 
                  <SafeIcon icon={FiChevronRight} /> 
                </button> 
              ) : ( 
                <button 
                  type="submit" 
                  disabled={saving || !canProceed()} 
                  className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2" 
                > 
                  {saving ? ( 
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> 
                  ) : ( 
                    <SafeIcon icon={FiSave} /> 
                  )} 
                  <span>{habit ? 'Update' : 'Create'} Habit</span> 
                </button> 
              )} 
            </div> 
          </div> 
        </form> 
      </motion.div> 
    </div> 
  )
}

export default HabitForm